import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { CartIcons, CloseIcons, TrashIcons } from '../../assets/svgIcons';

const CartContext = createContext(null);

/**
 * Normalize SVG so it follows `currentColor`
 */
const normalizeSvgColor = (svg) => {
  if (!svg) return svg;

  return svg
    .replace(/fill="[^"]*"/gi, '')
    .replace(/style="[^"]*"/gi, '')
    .replace('<svg', '<svg fill="currentColor"');
};

const PRELOADED = window.PSCFW_PRELOADED_SETTINGS || {};

export const CartProvider = ({ children }) => {
  /* ---------------- SETTINGS ---------------- */
  const [settings, setSettings] = useState(PRELOADED);

  /* ---------------- CART DATA ---------------- */
  const [cart, setCart] = useState({
    items: [],
    subtotal: 0,
    total: 0,
    count: 0,
    prdCount: 0,
    shipping: 0,
  });

  const [loading, setLoading] = useState(false);

  /* ---------------- ICONS ---------------- */
  const [iconSvg, setIconSvg] = useState(null);
  const [svgImg, setSvgImg] = useState(PRELOADED?.pscfw_cart_image);

  const [basketIconSvg, setBasketIconSvg] = useState(null);
  const [closeIconSvg, setCloseIconSvg] = useState(null);
  const [trashIconSvg, setTrashIconSvg] = useState(null);

  const resolveIconSvg = (icons, selectedValue) => {
    const matched = icons.find(icon => icon.value === selectedValue);
    return matched ? normalizeSvgColor(matched.icon) : null;
  };

  /* ---------------- FETCH CART ---------------- */
  const fetchCart = useCallback(async () => {
    setLoading(true);
  
    try {
      const response = await fetch(PSCFW_GLOBALS.ajax_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'get_cart_data',
        }),
      });
  
      const result = await response.json();
  
      if (!result.success) {
        throw new Error('AJAX failed');
      }
  
      const data = result.data;
  
      setCart({
        items: data.items || [],
        subtotal: data.subtotal || 0,
        total: data.total || 0,
        count: data.count || 0,
        prdCount: data.prdCount || 0,
        shipping: data.shipping || 0,
        sliderProducts: data.sliderProducts || [],
      });
    } catch (error) {
      console.error('Failed to fetch cart data', error);
    } finally {
      setLoading(false);
    }
  }, []);


  /* ----- UPDATES PRODUCT COUNT ON CART EVENTS ----- */
  useEffect(() => {
    const onAddedToCart = () => {
      fetchCart();
    };
  
    // WooCommerce events
    jQuery(document.body).on('added_to_cart', onAddedToCart);
    jQuery(document.body).on('removed_from_cart', onAddedToCart);
    jQuery(document.body).on('wc_fragments_refreshed', onAddedToCart);
  
    return () => {
      jQuery(document.body).off('added_to_cart', onAddedToCart);
      jQuery(document.body).off('removed_from_cart', onAddedToCart);
      jQuery(document.body).off('wc_fragments_refreshed', onAddedToCart);
    };
  }, [fetchCart]);


  /* ---------------- FETCH CART ON LOAD ---------------- */
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);


  /* ---------------- SVG ICON ---------------- */
  // for cart basket icons
  useEffect(() => {
    setBasketIconSvg(
      resolveIconSvg(CartIcons, settings.basket_icon)
    );
  }, [settings.basket_icon]);

  // for close cart icons
  useEffect(() => {
    setCloseIconSvg(
      resolveIconSvg(CloseIcons, settings.close_icon)
    );
  }, [settings.close_icon]);

  // for delete product icons
  useEffect(() => {
    setTrashIconSvg(
      resolveIconSvg(TrashIcons, settings.trash_icon)
    );
  }, [settings.trash_icon]);


  /* AJAX CALL FOR UPDATING PRODUCT QUANTITY IN SIDECART */
  const updateQty = async (key, qty) => {
    setLoading(true);
  
    try {
      const res = await fetch(PSCFW_GLOBALS.ajax_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'pscfw_atcpro_qty_val',
          product_key: key,
          qty,
        }),
      });
  
      const json = await res.json();
      if (!json.success) throw new Error('Qty update failed');
  
      // Update React cart state
      await fetchCart();
  
      // Update Woo mini cart / side cart
      jQuery(document.body).trigger('added_to_cart', [
        json.data.fragments,
        json.data.cart_hash,
      ]);
  
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  /* AJAX CALL FOR DELETING PRODUCT FROM SIDECART */
  const removeItem = async (cartItemKey) => {
    setLoading(true);
  
    try {
      const res = await fetch(PSCFW_GLOBALS.ajax_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'pscfw_remove_cart_item',
          cart_item_key: cartItemKey,
        }),
      });
  
      const json = await res.json();
  
      if (!json.success) {
        throw new Error(json.data?.message || 'Remove failed');
      }
  
      // Refresh React cart state
      await fetchCart();
  
      // Refresh Woo fragments (mini cart, header count)
      jQuery(document.body).trigger('added_to_cart');
  
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addFromSlider = async (productId) => {
    setLoading(true);
  
    try {
      const res = await fetch(PSCFW_GLOBALS.ajax_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          action: 'pscfw_add_to_cart_slider_pro',
          product_id: productId,
          quantity: 1,
        }),
      });
  
      const json = await res.json();
      if (!json.success) throw new Error('Add to cart failed');
  
      // Refresh cart
      await fetchCart();
  
      // Trigger Woo updates (mini cart, header)
      jQuery(document.body).trigger('added_to_cart');
  
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        settings,
        setSettings,
        cart,
        fetchCart,
        loading,
        svgImg,
        updateQty,
        removeItem,
        addFromSlider,
        basketIconSvg,
        closeIconSvg,
        trashIconSvg,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

/* ---------------- HOOK ---------------- */
export const useSettings = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useSettings must be used inside CartProvider');
  }

  return context;
};
